import { useEffect, useState } from "react";
import { ShoppingBag, Star } from "lucide-react";
import { trustStats } from "../lib/api";

/**
 * TrustBadges — compact live social-proof strip.
 *
 * Renders two pills, both backed by REAL data:
 *   - "Y orders / 30D" (actual paid-order count from the DB)
 *   - Star rating + review count (live Google Business reviews, if cached)
 *
 * Polls /api/trust/stats every 60s. Gracefully hides if the endpoint
 * fails or returns zero across the board.
 */
export function TrustBadges({ variant = "default", className = "" }) {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const s = await trustStats();
        if (!cancelled) setStats(s);
      } catch (_e) {
        if (!cancelled) setStats(null);
      }
    };
    load();
    const iv = setInterval(load, 60000);
    return () => { cancelled = true; clearInterval(iv); };
  }, []);

  if (!stats) return null;
  const showOrders = (stats.orders_last_30d || 0) > 0;
  const showReviews = stats.reviews_avg && (stats.reviews_total || 0) > 0;
  if (!showOrders && !showReviews) return null;

  const pill = variant === "compact"
    ? "inline-flex items-center gap-1.5 px-2.5 py-1 border border-[#222] bg-black/40 text-[10px] font-mono tracking-widest text-zinc-300"
    : "inline-flex items-center gap-2 px-3 py-1.5 border border-[#222] bg-black/40 text-[11px] font-mono tracking-widest text-zinc-300 hover:border-[var(--tc-red)] transition-colors";

  return (
    <div className={`flex flex-wrap gap-2 ${className}`} data-testid="trust-badges">
      {showOrders && (
        <span className={pill} data-testid="trust-orders">
          <ShoppingBag size={11} className="opacity-70" />
          <span><strong className="text-white">{stats.orders_last_30d}</strong> {stats.orders_last_30d === 1 ? "ORDER" : "ORDERS"} / 30D</span>
        </span>
      )}
      {showReviews && (
        <span className={pill} data-testid="trust-reviews">
          <Star size={11} className="text-amber-400 fill-amber-400" />
          <span>
            <strong className="text-white">{Number(stats.reviews_avg).toFixed(1)}</strong>
            <span className="opacity-60"> · {stats.reviews_total} REVIEWS</span>
          </span>
        </span>
      )}
    </div>
  );
}

export default TrustBadges;
